import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { CUSTOMER_POLICY_ROUTES } from '../data/customerPolicies';
import '../styles/DriverLegal.css';

const SUPPORT_EMAIL = 'support@easydroptaxi.com';
const SUPPORT_PHONE_DISPLAY = '+91 9629954544';
const SUPPORT_PHONE_TEL = 'tel:+919629954544';

const CustomerAccountDeletion = () => {
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState('request');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function post(body) {
    const response = await fetch('/.netlify/functions/request-account-deletion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      const failure = new Error(data.error || 'We could not verify that request. Nothing was deleted.');
      failure.code = data.code;
      throw failure;
    }
    return data;
  }

  async function handleRequest(event) {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!confirmed) {
      setError('Confirm that you want to request deletion before continuing.');
      return;
    }
    if (!/^\d{10}$/.test(mobile)) {
      setError('Enter the 10-digit mobile number saved on the Customer App account.');
      return;
    }
    setSubmitting(true);
    try {
      await post({
        action: 'start',
        email,
        mobile,
        confirmed: true,
      });
      setPhase('code');
      setMessage('If that email and mobile match a Customer App account, we sent an 8-digit verification code. Nothing is deleted until you enter the code.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(event) {
    event.preventDefault();
    setError('');
    setMessage('');
    setSubmitting(true);
    try {
      const result = await post({
        action: 'confirm',
        email,
        code,
      });
      if (result.completed !== true) {
        setError('Deletion was not confirmed. Your account was not reported as deleted.');
        return;
      }
      setPhase('done');
      setMessage('Your Customer App account has been deleted. Booking records needed for the business were kept with your name and mobile number removed.');
    } catch (confirmError) {
      setError(confirmError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="driver-legal-page">
      <SeoHead
        title="EasyDropTaxi Customer Account & Data Deletion"
        description="Request deletion of an EasyDropTaxi Customer App account. Google verification is required before any account data is deleted."
        path="/customer-account-deletion"
      />

      <section className="page-banner block-space text-white" style={{ backgroundColor: 'var(--secondary-color)' }}>
        <div className="container">
          <h1 className="display-4 fw-bold">Customer Account &amp; Data Deletion</h1>
          <p className="lead mb-0">
            Public page for the EasyDropTaxi Customer App. You do not need to open the app to start a request.
          </p>
        </div>
      </section>

      <section className="block-space">
        <div className="container">
          <div className="driver-legal-content">
            <p>
              This page is for the EasyDropTaxi Customer App account created with Google Sign-In.
              Signing out of the app does not delete the account.
            </p>

            <h2>What deletion removes</h2>
            <p>
              After your identity is verified, EasyDropTaxi deletes the Firebase sign-in account,
              the customer profile, notification tokens, and notification inbox for that account.
            </p>

            <h2>What may be retained</h2>
            <p>
              Trip, fare, and cancellation records can be retained for operations and accounting.
              Your name, mobile number, special requests, and guest-session link are removed from
              those Customer App booking records. The route and fare can remain.
            </p>

            <h2>How verification works</h2>
            <p>
              Enter the email and mobile number on the account. If they match one customer profile,
              we email an 8-digit code. The account is deleted only after you submit that code.
              Requests are handled as soon as the code is confirmed, and the code expires after 30 minutes.
              You can also delete the account immediately in the Customer App under Account, after signing in with Google again.
            </p>

            <p>
              Related policy:{' '}
              <Link to={CUSTOMER_POLICY_ROUTES.privacy}>Privacy Policy</Link>
            </p>

            <div className="driver-contact-panel">
              <p>
                <strong>Email:</strong>{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </p>
              <p className="mb-0">
                <strong>Phone:</strong>{' '}
                <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_DISPLAY}</a>
              </p>
            </div>

            {phase === 'done' ? (
              <p>{message}</p>
            ) : phase === 'code' ? (
              <form className="driver-deletion-form" onSubmit={handleConfirm} noValidate>
                <h2>Enter the verification code</h2>
                {message ? <p>{message}</p> : null}
                {error ? <p>{error}</p> : null}
                <div className="mb-3">
                  <label className="form-label" htmlFor="deletion-code">8-digit code</label>
                  <input
                    id="deletion-code"
                    className="form-control"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={8}
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 8))}
                    required
                  />
                </div>
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? 'Verifying…' : 'Verify and delete account'}
                </button>
              </form>
            ) : (
              <form className="driver-deletion-form" onSubmit={handleRequest} noValidate>
                <h2>Request account deletion</h2>
                {error ? <p>{error}</p> : null}
                <div className="mb-3">
                  <label className="form-label" htmlFor="account-email">Account email</label>
                  <input
                    id="account-email"
                    type="email"
                    className="form-control"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="account-mobile">Mobile number on the account</label>
                  <input
                    id="account-mobile"
                    type="tel"
                    className="form-control"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={10}
                    value={mobile}
                    onChange={(event) => setMobile(event.target.value.replace(/\D/g, '').slice(0, 10))}
                    required
                  />
                </div>
                <div className="form-check mb-3">
                  <input
                    id="confirm-deletion"
                    className="form-check-input"
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="confirm-deletion">
                    I understand this requests permanent deletion of my EasyDropTaxi Customer App account and is not the same as signing out.
                  </label>
                </div>
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? 'Sending code…' : 'Send verification code'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default CustomerAccountDeletion;
